import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listSuppliers } from '../../lib/api';
import { PageHeader } from './_shared';
import { NewReturnDialog, SupplierReturnsPanel } from './return-editor';

/**
 * Standalone Returns-to-Supplier page — the same panel Payables renders,
 * full-width, plus a deep link: the PO detail's "Return to supplier" button
 * arrives as `?po=<id>&supplier=<id>` and auto-opens the entry dialog with
 * both pickers pre-seeded.
 *
 * Route-level view perm + the RequireAccounting wrapper live in the router.
 */
export function SupplierReturnsPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramPo = searchParams.get('po') ?? '';
  const paramSupplier = searchParams.get('supplier') ?? '';
  const [deepLink, setDeepLink] = useState<{ supplierId: string; poId: string } | null>(null);

  // ONE-SHOT auto-open, keyed on the param values (not mount-only — React
  // Router re-renders in place, see orders.tsx). Clearing the params right
  // away means closing the dialog doesn't re-open it, while a fresh
  // navigation from a PO does.
  useEffect(() => {
    if (!paramPo || !paramSupplier) return;
    setDeepLink({ supplierId: paramSupplier, poId: paramPo });
    setSearchParams({}, { replace: true });
  }, [paramPo, paramSupplier, setSearchParams]);

  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => listSuppliers(true),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['supplier-returns'] });
    void qc.invalidateQueries({ queryKey: ['supplier-returnable'] });
    void qc.invalidateQueries({ queryKey: ['supplier-invoices'] });
    void qc.invalidateQueries({ queryKey: ['ap-aging-report'] });
    void qc.invalidateQueries({ queryKey: ['supplier-ledger-report'] });
    void qc.invalidateQueries({ queryKey: ['po-billing-summary'] });
    void qc.invalidateQueries({ queryKey: ['supplier-invoice-detail'] });
    void qc.invalidateQueries({ queryKey: ['po-billable-receipts'] });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Returns to Supplier"
        subtitle="Goods sent back to a supplier (RTS-) — stock leaves the branch; the value credits a bill, reverses GRNI, or stays as an open supplier credit."
      />

      <SupplierReturnsPanel suppliers={suppliersQ.data ?? []} onChanged={refresh} />

      {deepLink ? (
        <NewReturnDialog
          suppliers={suppliersQ.data ?? []}
          initialSupplierId={deepLink.supplierId}
          initialPoId={deepLink.poId}
          onClose={() => setDeepLink(null)}
          onSaved={() => {
            setDeepLink(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
